import apiClient from '../../../shared/api/client';
import { SearchFilters, PaginatedSearchResults } from '../types/search.types';

/**
 * Serialize a Date using local calendar date (YYYY-MM-DD) at UTC midnight,
 * matching the format used by the publish form — avoids timezone shift when
 * converting local midnight to UTC ISO string (e.g., UTC+7 00:00 → UTC 17:00 prev day).
 */
function toLocalDateISO(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T00:00:00.000Z`;
}

export const searchService = {
  /**
   * Search for offers based on filters with pagination
   */
  async searchOffers(filters: SearchFilters): Promise<PaginatedSearchResults> {
    const params: Record<string, string> = {};

    // Add keyword search
    if (filters.keyword?.trim()) {
      params.keyword = filters.keyword.trim();
    }

    // Add material filter
    if (filters.materialId) {
      params.materialId = filters.materialId;
    }
    if (filters.materialCategory) {
      params.materialCategory = filters.materialCategory;
    }
    if (filters.materialSubcategory) {
      params.materialSubcategory = filters.materialSubcategory;
    }

    // Add condition filter (convert 'all' to undefined, map to backend enum)
    if (filters.condition && filters.condition !== 'all') {
      params.condition = filters.condition.toUpperCase(); // NEW or USED
    }

    // Always add location coordinates for distance calculation in response
    if (filters.latitude !== undefined && filters.longitude !== undefined) {
      params.latitude = filters.latitude.toString();
      params.longitude = filters.longitude.toString();

      // Only add maxDistance filter if distance filtering is enabled
      if (filters.distanceEnabled) {
        params.maxDistance = (filters.maxDistance ?? 25).toString();
      }
    }

    // Add availability filters if enabled and at least one date is provided
    if (filters.availabilityEnabled && (filters.startDate || filters.endDate)) {
      if (filters.startDate) {
        params.startDate = toLocalDateISO(filters.startDate);
      }
      if (filters.endDate) {
        params.endDate = toLocalDateISO(filters.endDate);
      }
    }

    // Add pagination parameters
    if (filters.page !== undefined) {
      params.page = filters.page.toString();
    }
    if (filters.limit !== undefined) {
      params.limit = filters.limit.toString();
    }

    const response = await apiClient.get<PaginatedSearchResults>('/offers/search', {
      params,
    });

    return response;
  },
};
